import { useMemo, useState } from 'react'
import { Box, Button, Grid, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material'
import { SectionLoader } from 'Components/Section'
import { KoreanUniversityType } from 'Entities/koreanUniversity'
import { useKoreanLanguage2 } from '../Hooks/useKoreanLanguageForm'
import { useKoreanUniversities } from '../Hooks/useKoreanUniversities'

type RegionItem = {
  regionCode: number
  regionName: string
}

type FormState = {
  regionCode: number
  universityNameUz: string
  universityNameRu: string
  universityNameKo: string
  type: KoreanUniversityType | ''
}

type Props = {
  onCancel: () => void
}

const initialForm: FormState = {
  regionCode: 0,
  universityNameUz: '',
  universityNameRu: '',
  universityNameKo: '',
  type: '',
}

type TouchedFields = {
  regionCode: boolean
  type: boolean
  universityNameUz: boolean
  universityNameRu: boolean
  universityNameKo: boolean
}

const initialTouched: TouchedFields = {
  regionCode: false,
  type: false,
  universityNameUz: false,
  universityNameRu: false,
  universityNameKo: false,
}

const TYPE_OPTIONS: { value: KoreanUniversityType; label: string }[] = [
  { value: 'KOREAN_DEPARTMENT', label: 'Кафедра корейского языка' },
  { value: 'SECOND_FOREIGN_LANGUAGE', label: 'Второй иностранный язык' },
]

export const KoreanUniversityCreateForm = ({ onCancel }: Props) => {
  const [form, setForm] = useState<FormState>(initialForm)
  const [touched, setTouched] = useState<TouchedFields>(initialTouched)
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const { regions } = useKoreanLanguage2()
  const { createUniversityMutation } = useKoreanUniversities()

  const rawErrors = useMemo(() => {
    return {
      regionCode: !form.regionCode,
      type: !form.type,
      universityNameUz: !form.universityNameUz.trim(),
      universityNameRu: !form.universityNameRu.trim(),
      universityNameKo: !form.universityNameKo.trim(),
    }
  }, [form])

  const visibleErrors = {
    regionCode: rawErrors.regionCode && (touched.regionCode || submitAttempted),
    type: rawErrors.type && (touched.type || submitAttempted),
    universityNameUz: rawErrors.universityNameUz && (touched.universityNameUz || submitAttempted),
    universityNameRu: rawErrors.universityNameRu && (touched.universityNameRu || submitAttempted),
    universityNameKo: rawErrors.universityNameKo && (touched.universityNameKo || submitAttempted),
  }

  const isDisabled =
    rawErrors.regionCode ||
    rawErrors.type ||
    rawErrors.universityNameUz ||
    rawErrors.universityNameRu ||
    rawErrors.universityNameKo ||
    createUniversityMutation.isLoading

  const handleChange = (field: keyof FormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setForm(prev => ({
      ...prev,
      [field]: field === 'regionCode' ? Number(value) : value,
    }))
  }

  const handleBlur = (field: keyof TouchedFields) => () => {
    setTouched(prev => ({ ...prev, [field]: true }))
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitAttempted(true)
    if (
      rawErrors.regionCode ||
      rawErrors.type ||
      rawErrors.universityNameUz ||
      rawErrors.universityNameRu ||
      rawErrors.universityNameKo ||
      !form.type
    ) {
      return
    }

    createUniversityMutation.mutate(
      {
        regionCode: form.regionCode,
        universityNameUz: form.universityNameUz.trim(),
        universityNameRu: form.universityNameRu.trim(),
        universityNameKo: form.universityNameKo.trim(),
        type: form.type,
      },
      {
        onSuccess: () => {
          setForm(initialForm)
          setTouched(initialTouched)
          setSubmitAttempted(false)
        },
      },
    )
  }

  return (
    <SectionLoader isLoading={false} isFetching={false}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: '#fff',
        }}>
        <Stack spacing={3} component='form' onSubmit={handleSubmit}>
          <Box>
            <Typography variant='h5' fontWeight={700}>
              Добавить университет
            </Typography>
            <Typography variant='body2' color='text.secondary' mt={0.5}>
              Выберите регион и тип учреждения, введите название на трёх языках.
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label='Выберите регион'
                value={form.regionCode || ''}
                onChange={handleChange('regionCode')}
                onBlur={handleBlur('regionCode')}
                error={visibleErrors.regionCode}
                helperText={visibleErrors.regionCode ? 'Пожалуйста, выберите регион.' : ' '}>
                {(regions || []).map((region: RegionItem) => (
                  <MenuItem key={region.regionCode} value={region.regionCode}>
                    {region.regionName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label='Тип'
                value={form.type}
                onChange={handleChange('type')}
                onBlur={handleBlur('type')}
                error={visibleErrors.type}
                helperText={visibleErrors.type ? 'Выберите тип.' : ' '}>
                {TYPE_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='Название (UZ)'
                value={form.universityNameUz}
                onChange={handleChange('universityNameUz')}
                onBlur={handleBlur('universityNameUz')}
                error={visibleErrors.universityNameUz}
                helperText={visibleErrors.universityNameUz ? 'Обязательное поле.' : ' '}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='Название (RU)'
                value={form.universityNameRu}
                onChange={handleChange('universityNameRu')}
                onBlur={handleBlur('universityNameRu')}
                error={visibleErrors.universityNameRu}
                helperText={visibleErrors.universityNameRu ? 'Обязательное поле.' : ' '}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label='Название (KO)'
                value={form.universityNameKo}
                onChange={handleChange('universityNameKo')}
                onBlur={handleBlur('universityNameKo')}
                error={visibleErrors.universityNameKo}
                helperText={visibleErrors.universityNameKo ? 'Обязательное поле.' : ' '}
              />
            </Grid>
          </Grid>

          <Stack direction='row' spacing={1.5} justifyContent='flex-end'>
            <Button variant='outlined' color='inherit' onClick={onCancel} disabled={createUniversityMutation.isLoading}>
              Отмена
            </Button>
            <Button type='submit' variant='contained' disabled={isDisabled}>
              {createUniversityMutation.isLoading ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </SectionLoader>
  )
}
